import React, { useState } from "react";
import { AlertTriangle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";
import CartonTracker from "@/pages/sticks/CartonTracker";
import StickInventory from "@/pages/sticks/StickInventory";

function normOrg(s) {
  return (s || "").trim().toLowerCase().replace(/\s+/g, " ").replace(/\s?-\s?/g, "-");
}

export default function SticksPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState("carton");

  // Access guard (front-end mirror of backend rule)
  const isStaff = user?.role === "admin" || user?.role === "warehouse_staff";
  const isTmobile = user?.role === "client_user" && normOrg(user?.client_name) === "t-mobile";
  const allowed = isStaff || isTmobile;

  if (!allowed) {
    return (
      <div className="mx-auto max-w-md rounded-xl border border-amber-200 bg-amber-50 p-8 text-center" data-testid="sticks-access-restricted">
        <AlertTriangle className="mx-auto h-8 w-8 text-amber-500" />
        <h2 className="mt-3 text-lg font-semibold text-amber-900">Access Restricted</h2>
        <p className="mt-2 text-sm text-amber-800">
          The Sticks module is only available to Dolphin staff and T-Mobile users. If you believe this is a mistake, please contact your administrator.
        </p>
      </div>
    );
  }

  const canWrite = isStaff; // client_users have read-only access

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Sticks</h2>
          <p className="text-muted-foreground">Carton tracking & stick inventory management</p>
        </div>
        <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1">
          <button
            onClick={() => setTab("carton")}
            className={cn(
              "rounded-md px-4 py-1.5 text-sm font-medium transition-colors",
              tab === "carton" ? "bg-slate-900 text-white shadow-sm" : "text-slate-600 hover:text-slate-900"
            )}
            data-testid="sticks-tab-carton"
          >
            Carton Tracker
          </button>
          <button
            onClick={() => setTab("inventory")}
            className={cn(
              "rounded-md px-4 py-1.5 text-sm font-medium transition-colors",
              tab === "inventory" ? "bg-slate-900 text-white shadow-sm" : "text-slate-600 hover:text-slate-900"
            )}
            data-testid="sticks-tab-inventory"
          >
            Stick Inventory
          </button>
        </div>
      </div>

      {tab === "carton" ? <CartonTracker canWrite={canWrite} /> : <StickInventory canWrite={canWrite} />}
    </div>
  );
}
